/**
 * Authentication Manager
 * Handles separate authentication for Admin and MI (Mobile) systems
 * Ensures both systems can work independently without conflicts
 */

export enum AuthSystem {
  ADMIN = 'admin',
  MI = 'mi',
  FRANCHISE = 'franchise',
}

export interface AuthToken {
  token: string;
  system: AuthSystem;
  expiresAt?: number;
}

class AuthenticationManager {
  private readonly ADMIN_TOKEN_KEY = 'token';
  private readonly MI_TOKEN_KEY = 'mi_token';
  private readonly FRANCHISE_TOKEN_KEY = 'franchise_token';
  private readonly ADMIN_REFRESH_KEY = 'admin_refresh_token';
  private readonly MI_REFRESH_KEY = 'mi_refresh_token';
  private readonly FRANCHISE_REFRESH_KEY = 'franchise_refresh_token';
  private readonly ADMIN_AUTO_LOGIN_KEY = 'autoLogin';
  private readonly FRANCHISE_AUTO_LOGIN_KEY = 'franchiseAutoLogin';
  private readonly ADMIN_DEVICE_ID_KEY = 'admin_device_id';
  private readonly MI_SESSION_KEYS = [
    'mi_tasks_active_tab',
    'mi_history_search_text',
    'mi_user',
    'mi_device_id',
  ];

  private isTokenWithinLifetime(token: string): boolean {
    const parts = token.split('.');
    // Keep supporting opaque tokens used by older or external auth systems.
    if (parts.length !== 3) {
      return true;
    }

    try {
      const normalizedPayload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const paddedPayload = normalizedPayload.padEnd(
        normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
        '=',
      );
      const payload = JSON.parse(atob(paddedPayload)) as { exp?: number };
      return typeof payload.exp !== 'number' || payload.exp * 1000 > Date.now();
    } catch {
      // The server remains the source of truth. A malformed local payload is
      // still sent for validation instead of being treated as a logout event.
      return true;
    }
  }

  private getUsableToken(system: AuthSystem): string | null {
    const token = this.getToken(system);
    return token && this.isTokenWithinLifetime(token) ? token : null;
  }

  /**
   * Get token for specific system
   */
  getToken(system: AuthSystem): string | null {
    switch (system) {
      case AuthSystem.ADMIN:
        return localStorage.getItem(this.ADMIN_TOKEN_KEY);
      case AuthSystem.MI:
        return localStorage.getItem(this.MI_TOKEN_KEY);
      case AuthSystem.FRANCHISE:
        return localStorage.getItem(this.FRANCHISE_TOKEN_KEY);
      default:
        return null;
    }
  }

  /**
   * Stable, non-secret identifier used to describe the browser installation
   * in authentication history. It intentionally survives logout.
   */
  getOrCreateAdminDeviceId(): string {
    const existing = localStorage.getItem(this.ADMIN_DEVICE_ID_KEY)?.trim();
    if (existing) {
      return existing;
    }

    const generated =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
    localStorage.setItem(this.ADMIN_DEVICE_ID_KEY, generated);
    return generated;
  }

  getAdminDeviceName(): string {
    if (typeof navigator === 'undefined') {
      return 'Web browser';
    }

    const userAgent = navigator.userAgent || '';
    const browser = /Edg\//.test(userAgent)
      ? 'Edge'
      : /Firefox\//.test(userAgent)
        ? 'Firefox'
        : /Chrome\//.test(userAgent)
          ? 'Chrome'
          : /Safari\//.test(userAgent)
            ? 'Safari'
            : 'Web browser';
    const platform = navigator.platform || 'Unknown platform';
    return `${browser} on ${platform}`.slice(0, 255);
  }

  /**
   * Set token for specific system
   */
  setToken(system: AuthSystem, token: string): void {
    switch (system) {
      case AuthSystem.ADMIN:
        localStorage.setItem(this.ADMIN_TOKEN_KEY, token);
        break;
      case AuthSystem.MI:
        localStorage.setItem(this.MI_TOKEN_KEY, token);
        break;
      case AuthSystem.FRANCHISE:
        localStorage.setItem(this.FRANCHISE_TOKEN_KEY, token);
        break;
    }
  }

  /**
   * Get refresh token for specific system
   */
  getRefreshToken(system: AuthSystem): string | null {
    switch (system) {
      case AuthSystem.ADMIN:
        return localStorage.getItem(this.ADMIN_REFRESH_KEY);
      case AuthSystem.MI:
        return localStorage.getItem(this.MI_REFRESH_KEY);
      case AuthSystem.FRANCHISE:
        return localStorage.getItem(this.FRANCHISE_REFRESH_KEY);
      default:
        return null;
    }
  }

  /**
   * Set refresh token for specific system
   */
  setRefreshToken(system: AuthSystem, token: string): void {
    switch (system) {
      case AuthSystem.ADMIN:
        localStorage.setItem(this.ADMIN_REFRESH_KEY, token);
        break;
      case AuthSystem.MI:
        localStorage.setItem(this.MI_REFRESH_KEY, token);
        break;
      case AuthSystem.FRANCHISE:
        localStorage.setItem(this.FRANCHISE_REFRESH_KEY, token);
        break;
    }
  }

  /**
   * Clear authentication for specific system
   */
  clearAuth(system: AuthSystem): void {
    switch (system) {
      case AuthSystem.ADMIN:
        localStorage.removeItem(this.ADMIN_TOKEN_KEY);
        localStorage.removeItem(this.ADMIN_REFRESH_KEY);
        localStorage.removeItem(this.ADMIN_AUTO_LOGIN_KEY);
        // Clear permissions when logging out from admin system
        import('@/services/permission/storageService').then(({ permissionStorageService }) => {
          // Do not let a slow dynamic import clear the cache of a session that
          // completed login after this logout operation started.
          if (!this.getToken(AuthSystem.ADMIN)) {
            permissionStorageService.clearPermissions();
          }
        });
        // Notify the permission layer without importing its store back into the auth service.
        // This keeps auth and permission state decoupled and avoids a runtime module cycle.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('admin-auth-cleared'));
        }
        break;
      case AuthSystem.MI:
        localStorage.removeItem(this.MI_TOKEN_KEY);
        localStorage.removeItem(this.MI_REFRESH_KEY);
        // Clear MI-specific session data
        this.MI_SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
        break;
      case AuthSystem.FRANCHISE:
        localStorage.removeItem(this.FRANCHISE_TOKEN_KEY);
        localStorage.removeItem(this.FRANCHISE_REFRESH_KEY);
        localStorage.removeItem(this.FRANCHISE_AUTO_LOGIN_KEY);
        break;
    }
  }

  /**
   * Check if user is authenticated for specific system
   */
  isAuthenticated(system: AuthSystem): boolean {
    return this.getUsableToken(system) !== null;
  }

  /**
   * Used by async consumers to discard responses from a session that was
   * replaced while their request was in flight.
   */
  isCurrentToken(system: AuthSystem, token: string | null | undefined): boolean {
    return Boolean(token && this.getToken(system) === token);
  }

  /**
   * Get appropriate token based on API endpoint
   */
  getTokenForEndpoint(endpoint: string): string | null {
    // Normalize to avoid accidental matches on query strings
    const path = (() => {
      try {
        // If endpoint is absolute URL
        const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
        const url = new URL(endpoint, origin);
        return url.pathname;
      } catch {
        // Fallback if endpoint is relative path
        return endpoint;
      }
    })();

    const normalizedPath = path.toLowerCase();
    const currentRoute = typeof window !== 'undefined' ? window.location.pathname : '';
    const isFranchiseContext =
      currentRoute === '/franchise' || currentRoute.startsWith('/franchise/');

    const franchiseApiPrefixes = ['/api/franchise-analytics', '/api/franchisee-accounts'];

    // Public login requests must never inherit a stale Authorization header.
    // Re-authentication replaces the old single-device session on the server.
    const publicLoginPaths = new Set([
      '/api/auth/login',
      '/api/mi-mobile/auth/login',
      '/api/franchisee-accounts/login',
      '/api/fgshop/auth/login',
    ]);
    if (publicLoginPaths.has(normalizedPath)) {
      return null;
    }

    if (franchiseApiPrefixes.some((prefix) => normalizedPath.startsWith(prefix))) {
      return this.getUsableToken(isFranchiseContext ? AuthSystem.FRANCHISE : AuthSystem.ADMIN);
    }

    // Admin Mobile User Management endpoints (managed from admin portal)
    // These must use the Admin token even though the path contains "/mobile"
    if (path.includes('/api/mobile/users')) {
      return this.getUsableToken(AuthSystem.ADMIN);
    }

    // MI mobile app endpoints - explicitly scoped
    if (path.includes('/api/mi-mobile/') || path.includes('/mi/')) {
      return this.getUsableToken(AuthSystem.MI);
    }

    // Franchise endpoints
    if (normalizedPath.includes('/franchisee-account')) {
      return this.getUsableToken(isFranchiseContext ? AuthSystem.FRANCHISE : AuthSystem.ADMIN);
    }
    // Admin endpoints
    return this.getUsableToken(AuthSystem.ADMIN);
  }

  /**
   * Clear all authentication data (all systems)
   */
  clearAllAuth(): void {
    this.clearAuth(AuthSystem.ADMIN);
    this.clearAuth(AuthSystem.MI);
    this.clearAuth(AuthSystem.FRANCHISE);
  }

  /**
   * Get authentication headers for API request
   */
  getAuthHeaders(endpoint: string): Record<string, string> {
    const token = this.getTokenForEndpoint(endpoint);
    const deviceId = localStorage.getItem('mi_device_id');
    const headers: Record<string, string> = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Add device ID header for MI endpoints
    if (endpoint.includes('/api/mobile/') && deviceId) {
      headers['X-Device-Id'] = deviceId;
    }

    return headers;
  }

  /**
   * Debug helper to log current auth state
   */
  debugAuthState(): void {
    console.log('Authentication State:', {
      admin: {
        hasToken: this.isAuthenticated(AuthSystem.ADMIN),
        autoLogin: localStorage.getItem(this.ADMIN_AUTO_LOGIN_KEY),
      },
      mi: {
        hasToken: this.isAuthenticated(AuthSystem.MI),
        sessionData: this.MI_SESSION_KEYS.reduce(
          (acc, key) => ({
            ...acc,
            [key]: localStorage.getItem(key),
          }),
          {},
        ),
      },
      franchise: {
        hasToken: this.isAuthenticated(AuthSystem.FRANCHISE),
        autoLogin: localStorage.getItem(this.FRANCHISE_AUTO_LOGIN_KEY),
      },
    });
  }
}

// Export singleton instance
export const authManager = new AuthenticationManager();

// Export for backward compatibility
export default authManager;
